import React, {useState, useEffect} from 'react';
import {useNavigate, Routes, Route} from 'react-router';
import {
    Container,
    Typography,
    Grid,
    Box,
    CircularProgress,
    Alert,
    Card,
    CardContent,
    CardActions,
    Button,
    Chip,
    LinearProgress,
    TextField,
    InputAdornment,
    Tabs,
    Tab,
    Divider,
    IconButton,
    Tooltip,
} from '@mui/material';
import {
    School as SchoolIcon,
    EmojiEvents as TrophyIcon,
    Stars as CoinsIcon,
    TrendingUp as LevelIcon,
    Quiz as QuizIcon,
    Search as SearchIcon,
    Add as AddIcon,
    Person as PersonIcon,
    Clear as ClearIcon,
} from '@mui/icons-material';
import SendIcon from '@mui/icons-material/Send';
import {createApiClient} from '../../services/ApiClient.jsx';
import { GamificationCard } from './GamificationCard';
import { AchievementsModal } from '../../components/AchievementsModal';
import { LeaderboardModal } from '../../components/LeaderboardModal';
import {CourseSearch} from "./CourseSearch.jsx";
import { StudentGamification } from './StudentGamification';
import LyingQuantino from '../../components/LyingQuantino/LyingQuantino.jsx';


const StudentDashboard = ({accessToken, claims}) => {
    const navigate = useNavigate();
    const [courses, setCourses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentTab, setCurrentTab] = useState(0);
    
    // Gamification modals
    const [achievementsOpen, setAchievementsOpen] = useState(false);
    const [leaderboardOpen, setLeaderboardOpen] = useState(false);

    const apiClient = createApiClient(accessToken);
    const username = claims?.username || claims?.name || 'Student';
    const userId = claims?.sub; // User ID for gamification

    const fetchEnrolledCourses = async () => {
        try {
            const response = await apiClient.get('/api/courses/enrolled');
            setCourses(response.courses || []);
            setError(null);
        } catch (err) {
            console.error('Error fetching enrolled courses:', err);
            setError('Failed to load courses. Please try again later.');
        }
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                await fetchEnrolledCourses();
            } catch (err) {
                console.error('Error fetching dashboard data:', err);
                setError('Failed to load dashboard data. Please try again later.');
            } finally {
                setLoading(false);
            }
        };

        if (accessToken) {
            fetchData();
        }
    }, [accessToken]);

    const handleStartCourse = (courseId) => {
        navigate(`/student/course/${courseId}`);
    };

    const handleTabChange = (event, newValue) => {
        setCurrentTab(newValue);
    };

    const handleCourseEnrolled = (enrolledCourse) => {
        // Refresh the courses list when a new course is enrolled
        fetchEnrolledCourses();
    };

    if (loading) {
        return (
            <Container maxWidth="xl" sx={{mt: 1, mb: 1}}>
                <Box sx={{display: 'flex', justifyContent: 'center', my: 4}}>
                    <CircularProgress/>
                </Box>
            </Container>
        );
    }

    return (
        <Container maxWidth="xl" sx={{mt: 8, pb: 10}}>
            {/* Welcome Header */}
            <Box sx={{mb: 4}}>
                <Typography variant="h4" gutterBottom>
                    Welcome back, {username}! 🎓
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    Continue your learning journey and unlock achievements!
                </Typography>
            </Box>

            {/* Error Alert */}
            {error && (
                <Alert severity="error" sx={{mb: 4}}>
                    {error}
                </Alert>
            )}

            <Grid container spacing={4}>
                {/* Left Column - Main Content */}
                <Grid size={{xs: 12, md: 8}}>
                    {/* Tab Navigation */}
                    <Box sx={{borderBottom: 1, borderColor: 'divider', mb: 4}}>
                        <Tabs
                            value={currentTab}
                            onChange={handleTabChange}
                            aria-label="student dashboard tabs"
                            sx={{
                                '& .MuiTab-root': {
                                    textTransform: 'none',
                                    fontSize: '1rem',
                                },
                            }}
                        >
                            <Tab
                                label="My Courses"
                                icon={<SchoolIcon/>}
                                iconPosition="start"
                                sx={{minHeight: 48}}
                            />
                            <Tab
                                label="Find Courses"
                                icon={<SearchIcon/>}
                                iconPosition="start"
                                sx={{minHeight: 48}}
                            />
                        </Tabs>
                    </Box>

                    {/* Tab Content */}
                    {currentTab === 0 && (
                        <Box>
                            {courses.length === 0 ? (
                                <Card>
                                    <CardContent sx={{textAlign: 'center', py: 8}}>
                                        <SchoolIcon sx={{fontSize: 80, color: 'text.secondary', mb: 3}}/>
                                        <Typography variant="h5" gutterBottom>
                                            No Courses Yet
                                        </Typography>
                                        <Typography variant="body1" color="text.secondary"
                                                    sx={{mb: 3, maxWidth: 400, mx: 'auto'}}>
                                            You're not enrolled in any courses yet. Browse available courses or contact
                                            your teacher to get enrolled!
                                        </Typography>
                                        <Button
                                            variant="contained"
                                            onClick={() => setCurrentTab(1)}
                                            sx={{
                                                px: 4,
                                                py: 1.5,
                                                borderRadius: 2,
                                                textTransform: 'none',
                                            }}
                                        >
                                            Find Courses
                                        </Button>
                                    </CardContent>
                                </Card>
                            ) : (
                                <Grid container spacing={3}>
                                    {courses.map((course) => (
                                        <Grid size={{xs: 12, sm: 6}} key={course.id}>
                                            <Card
                                                elevation={2}
                                                sx={{
                                                    height: '100%',
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    transition: 'transform 0.2s, box-shadow 0.2s',
                                                    '&:hover': {
                                                        transform: 'translateY(-4px)',
                                                        boxShadow: 6,
                                                    },
                                                }}
                                            >
                                                <CardContent sx={{flexGrow: 1, p: 3, pb:0}}>
                                                    <Typography variant="h6" gutterBottom>
                                                        {course.title}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary" sx={{mb: 3}}>
                                                        {course.description || 'No description available'}
                                                    </Typography>

                                                    <Box sx={{display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap'}}>
                                                        <Chip
                                                            icon={<QuizIcon/>}
                                                            label={`${course.topic_count || 0} Topics`}
                                                            size="small"
                                                            variant="outlined"
                                                        />
                                                        <Chip
                                                            icon={<QuizIcon/>}
                                                            label={`${course.question_count || 0} Questions`}
                                                            size="small"
                                                            variant="outlined"
                                                        />
                                                    </Box>
                                                </CardContent>

                                                <CardActions sx={{p: 3, pt: 0}}>
                                                    <Button
                                                        variant="contained"
                                                        fullWidth
                                                        onClick={() => handleStartCourse(course.id)}
                                                        sx={{
                                                            py: 1.5,
                                                            textTransform: 'none',
                                                            fontSize: '1rem',
                                                            background: theme => `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                                                            color: 'white',
                                                            fontWeight: 'bold',
                                                            boxShadow: 3,
                                                            '&:hover': {
                                                                background: theme => `linear-gradient(90deg, ${theme.palette.primary.dark} 0%, ${theme.palette.secondary.dark} 100%)`,
                                                                transform: 'translateY(-2px)',
                                                                boxShadow: 6,
                                                            }
                                                        }}
                                                    >
                                                        Open Course
                                                    </Button>
                                                </CardActions>
                                            </Card>
                                        </Grid>
                                    ))}
                                </Grid>
                            )}
                        </Box>
                    )}

                    {currentTab === 1 && (
                        <CourseSearch
                            accessToken={accessToken}
                            onCourseEnrolled={handleCourseEnrolled}
                        />
                    )}
                </Grid>

                {/* Right Column - Gamification Progress */}
                <Grid size={{xs: 12, md: 4}}>
                    <GamificationCard 
                        userId={userId}
                        accessToken={accessToken}
                        onViewLeaderboard={() => setLeaderboardOpen(true)}
                        onViewAchievements={() => setAchievementsOpen(true)}
                    />
                </Grid>
            </Grid>

            {/* Gamification Modals */}
            <AchievementsModal
                open={achievementsOpen}
                onClose={() => setAchievementsOpen(false)}
                userId={userId}
                accessToken={accessToken}
            />
            <LeaderboardModal
                open={leaderboardOpen}
                onClose={() => setLeaderboardOpen(false)}
                userId={userId}
                accessToken={accessToken}
            />
            <Box sx={{ position: 'fixed', bottom: 41, right: 10, zIndex: 1000 }}>
                <LyingQuantino user={claims} userId={userId} accessToken={accessToken} />
            </Box>
        </Container>
    );
};

export default StudentDashboard;